import React, { useState } from 'react';
import { createReactEditorJS } from 'react-editor-js';
import axios from 'axios';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Image from '@editorjs/image';
import Embed from '@editorjs/embed';
import { host } from '../script/variables';
import AsyncCreatableSelect from 'react-select/async-creatable';
import uploadViaPresign from '../script/uploadViaPresignV2';

const ReactEditorJS = createReactEditorJS();

const BlogEditor = ({ title, titleAv, thumbnail }) => {
  const editorCore = React.useRef(null);
  const [tags,       setTags]       = useState([]);
  const [taglist,    setTagList]    = useState([{ value: 'dosa', label: 'dosa' }]);
  const [isSaving,   setIsSaving]   = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'draft' | 'published'
  const token = localStorage.getItem('token');

  /* ── S3 image upload ── */
  const uploadFile = async (blogimg) => {
    if (!blogimg) { alert('Please select a file to upload.'); return; }
    return uploadViaPresign(blogimg, 'moment');
  };

  /* ── Tags ── */
  const handleTagSelect = (selected) => {
    if (tags.length >= 3) { alert('Only 3 tags are allowed'); return; }
    setTags(selected);
  };

  const fetchtaglist = async (inp) => {
    try {
      const response = await fetch(`${host}/blog/fetchtags?tname=${inp}`, {
        method:  'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (response.status === 200) {
        const data = await response.json();
        return data.map((d) => ({ value: d.name, label: d.name }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  /* ── Editor init ── */
  const handleInitialize = React.useCallback((instance) => {
    editorCore.current = instance;
  }, []);

  /* ── Shared submit logic ── */
  const submitBlog = async (status) => {
    if (title === '')  { alert('Please enter title'); return; }
    if (!titleAv)      { alert('Title is already used. Please change the title'); return; }

    if (!thumbnail) {
      const proceed = window.confirm(
        'No thumbnail has been uploaded yet. Continue without a thumbnail?'
      );
      if (!proceed) return;
    }

    setIsSaving(true);
    setSubmitStatus(null);

    const savedData = await editorCore.current.save();

    const payload = {
      title,
      body:      savedData.blocks,
      status,
      tags,
      thumbnail: thumbnail || '',
    };

    try {
      const response = await fetch(`${host}/blog/add`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify(payload),
      });

      if (response.ok) {
        setSubmitStatus(status); // 'draft' or 'published'
      } else {
        console.log('Submit failed:', response.status);
      }
    } catch (error) {
      console.error('Error submitting blog:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave      = () => submitBlog('published');
  const handleSaveDraft = () => submitBlog('draft');

  /* ── EditorJS image uploader ── */
  const imageUpload = async (file) => {
    try {
      const imageUrl = await uploadFile(file);
      return { success: 1, file: { url: imageUrl } };
    } catch (error) {
      console.error('Image upload failed:', error);
      return { success: 0, message: 'Image upload failed' };
    }
  };

  const generateRandomNumber = () => Math.floor(Math.random() * 1000) + 1;

  const EDITOR_JS_TOOLS = {
    header: {
      class:         Header,
      inlineToolbar: true,
      config: {
        placeholder:  'Enter a heading',
        levels:       [1, 2, 3, 4, 5, 6],
        defaultLevel: 2,
      },
    },
    list: {
      class:         List,
      inlineToolbar: true,
    },
    image: {
      class: Image,
      config: {
        uploader: {
          uploadByFile(file) { return imageUpload(file); },
          uploadByUrl(url)   { return { success: 1, file: { url } }; },
        },
      },
    },
    embed: {
      class:  Embed,
      config: { services: { youtube: true, coub: true } },
    },
  };

  return (
    <>
      <div className="blog-write-editor">
        <ReactEditorJS onInitialize={handleInitialize} tools={EDITOR_JS_TOOLS} />
      </div>

      <div className="blog-write-tag">
        <AsyncCreatableSelect
          placeholder="Add tags"
          loadOptions={fetchtaglist}
          value={tags}
          isMulti
          onChange={handleTagSelect}
        />
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
        {isSaving ? 'Publishing…' : 'Publish'}
      </button>

      <button className="btn btn-primary" onClick={handleSaveDraft} disabled={isSaving}>
        {isSaving ? 'Saving…' : 'Save Draft'}
      </button>

      {submitStatus === 'published' && (
        <p className="blog-submit-status blog-submit-status--published">
          Blog submitted for review.
        </p>
      )}

      {submitStatus === 'draft' && (
        <p className="blog-submit-status blog-submit-status--draft">
          Saved as draft.
        </p>
      )}
    </>
  );
};

export default BlogEditor;